
import React from 'react';
import { usePermisos } from '../context/PermisosContext';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import VisitasSparkline from './charts/VisitasSparkline';
import ObrasSparkline from './charts/ObrasSparkline';
import MapaVisitasObras from './charts/MapaVisitasObras';


const DashboardHome = () => {
  const { 
    user, 
    visitas, 
    loadingVisitas, 
    funcionComercial, 
    obras, 
    loadingObras, 
    refreshVisitas, 
    refreshObras,
    tieneAccesoVisitas,
    tieneAccesoObras,
    loadingPermisos
  } = usePermisos();
  const navigate = useNavigate();
  const navigateToVisitas = () => {
    if (tieneAccesoVisitas) navigate('/dashboard/visitas');
  };
  const navigateToObras = () => {
    if (tieneAccesoObras) navigate('/dashboard/obras');
  };

  // Función para obtener el saludo según la hora actual
  const getSaludo = () => {
    const hora = new Date().getHours();
    
    if (hora >= 6 && hora < 12) {
      return "¡Buenos días";
    } else if (hora >= 12 && hora < 18) {
      return "¡Buenas tardes";
    } else {
      return "¡Buenas noches";
    }
  };

  // Función para obtener el ícono según la hora
  const getIconoSaludo = () => {
    const hora = new Date().getHours();
    
    if (hora >= 6 && hora < 12) {
      return "☀️";
    } else if (hora >= 12 && hora < 18) {
      return "🌤️";
    } else {
      return "🌙";
    }
  };

  if (!user) return (
    <div className="flex justify-center items-center h-64 no-select">
      <div className="flex flex-col items-center gap-3">
        <div className="loader border-t-4 border-blue-500 rounded-full w-10 h-10 animate-spin"></div>
        <p className="text-gray-600">Cargando dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 no-select">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8 mt-5 sm:mt-0">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">Dashboard</h1>
            <p className="text-gray-600 text-sm sm:text-base">Gestión de visitas y obras comerciales</p>
          </div>
          <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-xl shadow-sm border hover:shadow-lg hover:border-blue-200 transition-all duration-300 cursor-pointer">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {user.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="text-right">
              <p className="font-medium text-gray-800 text-sm sm:text-base truncate max-w-[150px] sm:max-w-none">
                {user.name}
              </p>
            </div>
          </div>
        </div>

        {/* Tarjeta de bienvenida */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-700 rounded-2xl p-6 sm:p-8 text-white mb-8 shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{getIconoSaludo()}</span>
                <h2 className="text-xl sm:text-2xl font-bold">
                  {getSaludo()}, {user.name}!
                </h2>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-blue-100">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M14.243 5.757a6 6 0 10-.986 9.284 1 1 0 111.087 1.678A8 8 0 1118 10a3 3 0 01-4.8 2.401A4 4 0 1114 10a1 1 0 102 0c0-1.537-.586-3.07-1.757-4.243zM12 10a2 2 0 10-4 0 2 2 0 004 0z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm break-all">{user.username}</p>
                </div>
                <div className="flex items-center gap-2 text-blue-100">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-medium">{funcionComercial || 'Función no asignada'}</p>
                </div>
              </div>
            </div>
            <div className="hidden sm:block">
              <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Tarjetas de estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Tarjeta de Visitas */}
          <div 
            onClick={navigateToVisitas} 
            className={`group bg-white rounded-2xl p-6 sm:p-8 shadow-sm border transition-all duration-300 relative
              ${!tieneAccesoVisitas && !loadingPermisos
                ? 'border-gray-200 opacity-50 cursor-not-allowed'
                : 'border-gray-200 hover:shadow-lg hover:border-blue-200 cursor-pointer'
              }`}
          >
            {!tieneAccesoVisitas && !loadingPermisos && (
              <div className="absolute inset-0 bg-gray-50/80 backdrop-blur-[1px] rounded-2xl flex items-center justify-center z-10">
                <div className="text-center px-4">
                  <svg className="w-12 h-12 mx-auto mb-3 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Acceso Restringido</p>
                  <p className="text-xs text-gray-500">No tienes permisos para ver visitas</p>
                </div>
              </div>
            )}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg">Visitas</h3>
                  <p className="text-gray-500 text-sm">Total disponibles</p>
                </div>
              </div>
              <div className="p-2 bg-green-50 rounded-lg">
                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            
            <div className="flex items-end justify-between">
              {loadingVisitas ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="text-gray-500 text-sm">Cargando...</span>
                </div>
              ) : (
                <div>
                  <span className="text-3xl sm:text-4xl font-bold text-gray-800 block">
                    {visitas.length.toLocaleString('es-ES')}
                  </span>
                  <span className="text-sm text-gray-500 mt-1 block">registros encontrados</span>
                </div>
              )}
            </div>
          </div>

          {/* Tarjeta de Obras */}
          <div 
            onClick={navigateToObras} 
            className={`group bg-white rounded-2xl p-6 sm:p-8 shadow-sm border transition-all duration-300 relative
              ${!tieneAccesoObras && !loadingPermisos
                ? 'border-gray-200 opacity-50 cursor-not-allowed'
                : 'border-gray-200 hover:shadow-lg hover:border-purple-200 cursor-pointer'
              }`}
          >
            {!tieneAccesoObras && !loadingPermisos && (
              <div className="absolute inset-0 bg-gray-50/80 backdrop-blur-[1px] rounded-2xl flex items-center justify-center z-10">
                <div className="text-center px-4">
                  <svg className="w-12 h-12 mx-auto mb-3 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Acceso Restringido</p>
                  <p className="text-xs text-gray-500">No tienes permisos para ver obras</p>
                </div>
              </div>
            )}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-50 rounded-xl group-hover:bg-purple-100 transition-colors">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-lg">Obras</h3>
                  <p className="text-gray-500 text-sm">Total disponibles</p>
                </div>
              </div>
              <div className="p-2 bg-green-50 rounded-lg">
                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </div>

            </div>
            
            <div className="flex items-end justify-between">
              {loadingObras ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                  <span className="text-gray-500 text-sm">Cargando...</span>
                </div>
              ) : (
                <div>
                  <span className="text-3xl sm:text-4xl font-bold text-gray-800 block">
                    {obras.length.toLocaleString('es-ES')}
                  </span>
                  <span className="text-sm text-gray-500 mt-1 block">registros encontrados</span>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Sparkline Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Sparkline de Visitas */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-blue-200 transition-all duration-300 relative group">
            <button
              onClick={refreshVisitas}
              className="absolute top-4 right-4 group-hover:duration-200 p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600"
              title="Actualizar datos de visitas"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <VisitasSparkline visitas={visitas} />
          </div>

          {/* Sparkline de Obras */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-purple-200 transition-all duration-300 relative group">
            <button
              onClick={refreshObras}
              className="absolute top-4 right-4 group-hover:duration-200 p-2 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-600"
              title="Actualizar datos de obras"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <ObrasSparkline obras={obras} />
          </div>
        </div>

        {/* Mapa de Visitas y Obras */}
        <div className="mb-6">
          <MapaVisitasObras visitas={visitas} obras={obras} />
        </div>

        {/* Footer informativo */}
        {/* <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-800 text-sm">Sistema de Gestión CRM</p>
                <p className="font-medium text-gray-800 text-sm">Sistema de Avisos CRM</p>
                <p className="text-gray-500 text-xs">Última actualización: {new Date().toLocaleDateString('es-ES')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>Sistema operativo</span>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default DashboardHome;
